import { LitElement, html, css } from 'lit';

import { openError } from '../../../../neb-dialog/neb-banner-state';
import { store } from '../../../../neb-redux/neb-redux-store';
import {
  CSS_COLOR_WHITE,
  CSS_SPACING,
  CSS_COLOR_HIGHLIGHT,
  CSS_FONT_WEIGHT_BOLD,
  CSS_COLOR_BLACK,
  CSS_COLOR_GREY_1,
} from '../../../../neb-styles/neb-variables';
import { BUTTON_ROLE } from '../neb-button';

import './neb-two-factor-auth';
import '../../../../../src/components/misc/neb-icon';

export const ELEMENTS = {
  twoFactorAuth: { id: 'neb-2fa-auth-page-two-factor-auth' },
  submitButton: { id: 'neb-2fa-auth-page-submit-button' },
  cancelButton: { id: 'neb-2fa-auth-page-cancel-button' },
};

export const AUTH_PAGE_TYPES = {
  TOTP: 'totp',
  SMS: 'sms',
};

export default class Neb2faAuthPage extends LitElement {
  static get properties() {
    return {
      error: { type: String },
      authType: { type: String },
      challengeParameters: { type: Object },
    };
  }

  constructor() {
    super();
    this.__initState();
    this.__initHandlers();
  }

  __initState() {
    this.error = '';
    this.authType = AUTH_PAGE_TYPES.TOTP;
    this.challengeParameters = null;

    this.onSubmit = () => {};

    this.onCancel = () => {};
  }

  __initHandlers() {
    this.__handlers = {
      codeChange: code => {
        if (code.length !== 6) {
          return;
        }

        this.onSubmit(code);
      },
      cancel: () => {
        this.onCancel();
      },
    };
  }

  __clearCodeInput() {
    const twoFactorAuth = this.shadowRoot.getElementById(
      ELEMENTS.twoFactorAuth.id,
    );

    if (twoFactorAuth) {
      setTimeout(() => {
        twoFactorAuth.clearCode();
      }, 1000);
    }
  }

  updated(changedProperties) {
    if (changedProperties.has('error') && this.error) {
      store.dispatch(openError(this.error));
      this.__clearCodeInput();
    }
  }

  static get styles() {
    return [
      css`
        .two-factor-auth-container {
          display: flex;
          flex-direction: column;
          align-items: flex-start;
          background-color: ${CSS_COLOR_WHITE};
          border-radius: 5px;
          width: fit-content;
        }

        .title {
          font-size: 24px;
          font-weight: ${CSS_FONT_WEIGHT_BOLD};
          margin-bottom: ${CSS_SPACING};
          color: ${CSS_COLOR_BLACK};
          text-align: left;
          width: 100%;
          margin-left: 0;
        }

        .subtitle {
          font-size: 16px;
          color: ${CSS_COLOR_GREY_1};
          text-align: left;
          margin-bottom: 20px;
          width: 100%;
          margin-left: 0;
        }

        .enter-code-label {
          font-size: 18px;
          font-weight: ${CSS_FONT_WEIGHT_BOLD};
          color: ${CSS_COLOR_BLACK};
          margin-top: 8px;
          margin-bottom: 8px;
          text-align: left;
          margin-left: 30px;
        }

        .code-input-row {
          margin-left: 42px;
        }

        .buttons-container {
          display: flex;
          justify-content: space-between;
          width: 100%;
          margin-top: 32px;
        }

        .authenticator-icon {
          width: 60px;
          height: 60px;
          margin-bottom: 15px;
          fill: ${CSS_COLOR_HIGHLIGHT};
        }
      `,
    ];
  }

  __computePhoneText() {
    let phoneText = 'Check your phone for a 6-digit verification code.';

    try {
      const phoneInfo = this.challengeParameters?.CODE_DELIVERY_DESTINATION;

      if (phoneInfo && phoneInfo.length >= 4) {
        const lastFour = phoneInfo.slice(-4);
        phoneText = `We've sent a code to a phone number ending in ${lastFour}.`;
      }
    } catch (e) {
      console.error(e);
    }

    return html`
      <div>${phoneText}</div>
      <div>You should receive a text message with the code shortly.</div>
    `;
  }

  render() {
    const hasError = !!this.error;
    const subtitleText =
      this.authType === AUTH_PAGE_TYPES.TOTP
        ? 'Check your Authenticator app for 6-digit code.'
        : this.__computePhoneText();
    const isAuthenticator = this.authType === AUTH_PAGE_TYPES.TOTP;

    return html`
      <div class="two-factor-auth-container">
        <div class="title">2-Step Verification</div>
        <div class="subtitle">${subtitleText}</div>
        <div class="enter-code-label">Enter Code</div>
        <div class="code-input-row">
          <neb-two-factor-auth
            id="${ELEMENTS.twoFactorAuth.id}"
            .onChange="${this.__handlers.codeChange}"
            ?authenticator="${isAuthenticator}"
            ?error="${hasError}"
          ></neb-two-factor-auth>
        </div>

        <div class="buttons-container">
          <neb-button
            id="${ELEMENTS.cancelButton.id}"
            label="Cancel"
            .role="${BUTTON_ROLE.OUTLINE}"
            .onClick="${this.__handlers.cancel}"
          ></neb-button>
        </div>
      </div>
    `;
  }
}

customElements.define('neb-2fa-auth-page', Neb2faAuthPage);
